import { Stack } from '@mui/material';
import Button from '@mui/material/Button';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../api/http';
import { AuthContext, AuthData } from '../../context/auth';
import { ClaimContext, ClaimData } from '../../context/claim';
import { ErrorContext, ErrorData } from '../../context/ErrorProvider';
import { PolicyContext, PolicyData } from '../../context/policy';
import {
	CLAIM_CLOSED_FAILURE_TOAST_MSG,
	CLAIM_CLOSED_SUCCESS_TOAST_MSG,
	CLAIM_CLOSE_BUTTON_TEXT,
	CLAIM_DISCARDED_FAILURE_TOAST_MSG,
	CLAIM_DISCARDED_SUCCESS_TOAST_MSG,
	CLAIM_DISCARD_BUTTON_TEXT,
	CLAIM_REOPEN_BUTTON_TEXT,
	CLAIM_REOPEN_FAILURE_TOAST_MSG,
	CLAIM_REOPEN_SUCCESS_TOAST_MSG,
} from '../../data/labelsAndMessages';
import DropdownButton from '../DropdownButton';
import { errorToast, successToast } from '../Toast';
import OpenClaimModal from './OpenClaimModal';

interface Props {
	authData: AuthData;
	claimData: ClaimData;
	errorData: ErrorData;
	policyData: PolicyData;
}

const ClaimActions = (props: Props) => {
	// Determine which page we are on
	const { claimData, authData } = props;
	const { hasPermission } = authData;
	const params = useParams();
	const navigate = useNavigate();

	const { claim, claimUpdateReq, setClaimUpdateReq, clear, setLoading } =
		claimData;

	const closeClaim = async () => {
		setLoading(true);
		try {
			claimUpdateReq.status = 'closed';
			await claimData.updateClaim(claim.locator, claimUpdateReq);
			successToast(CLAIM_CLOSED_SUCCESS_TOAST_MSG());
			setClaimUpdateReq({});
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast(CLAIM_CLOSED_FAILURE_TOAST_MSG());
		}
		setLoading(false);
	};

	const reopenClaim = async () => {
		setLoading(true);
		try {
			claimUpdateReq.status = 'open';
			await claimData.updateClaim(claim.locator, claimUpdateReq);
			successToast(CLAIM_REOPEN_SUCCESS_TOAST_MSG());
			setClaimUpdateReq({});
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast(CLAIM_REOPEN_FAILURE_TOAST_MSG());
		}
		setLoading(false);
	};

	const discardClaim = async () => {
		setLoading(true);
		try {
			await claimData.discardClaim(claim.locator);
			successToast(CLAIM_DISCARDED_SUCCESS_TOAST_MSG());
			// redirect back to policy claims
			navigate(`/policy/${params.policyId}/claims`);
			// clear claim context
			clear();
		} catch (error) {
			errorToast(CLAIM_DISCARDED_FAILURE_TOAST_MSG());
		}
		setLoading(false);
	};

	const hiddenOptions = [];

	if (claim.currentStatus === 'open') {
		return (
			<Stack direction='row' spacing={1}>
				{hasPermission('claim.update') && (
					<Button
						key={'close'}
						color='primary'
						onClick={closeClaim}
						variant='contained'
						sx={{ margin: '5px' }}
					>
						{CLAIM_CLOSE_BUTTON_TEXT()}
					</Button>
				)}
			</Stack>
		);
	} else if (claim.currentStatus === 'pending') {
		if (hasPermission('claim.discard')) {
			hiddenOptions.push({
				label: CLAIM_DISCARD_BUTTON_TEXT(),
				callback: discardClaim,
			});
		}

		return (
			<Stack direction='row' spacing={1}>
				{hasPermission('claim.update') && <OpenClaimModal />}
				<DropdownButton actions={hiddenOptions} />
			</Stack>
		);
	} else {
		// closed
		return (
			<Stack direction='row' spacing={1}>
				{hasPermission('claim.update') && (
					<Button
						color='primary'
						sx={{ margin: '5px' }}
						onClick={reopenClaim}
						variant='contained'
					>
						{CLAIM_REOPEN_BUTTON_TEXT()}
					</Button>
				)}
			</Stack>
		);
	}
};

function withContext<T>(Component: any) {
	return (
		props: Omit<T, 'authData' | 'claimData' | 'errorData' | 'policyData'>,
	) => (
		<ErrorContext.Consumer>
			{(errorData) => (
				<AuthContext.Consumer>
					{(authData) => (
						<ClaimContext.Consumer>
							{(claimData) => (
								<PolicyContext.Consumer>
									{(policyData) => (
										<Component
											{...props}
											claimData={claimData}
											authData={authData}
											errorData={errorData}
											policyData={policyData}
										/>
									)}
								</PolicyContext.Consumer>
							)}
						</ClaimContext.Consumer>
					)}
				</AuthContext.Consumer>
			)}
		</ErrorContext.Consumer>
	);
}

export default withContext<Props>(ClaimActions);
